import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Star, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import QuickViewModal from './QuickViewModal';

const SerenityProductCard = ({ product }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);
  
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  
  const productId = product._id || product.id;
  const isWishlisted = isInWishlist(productId);

  const displayPrice = product.salePrice || product.price;
  const hasDiscount = product.salePrice && product.salePrice < product.price;
  const discountPercent = hasDiscount
    ? Math.round(((product.price - product.salePrice) / product.price) * 100)
    : 0;

  return (
    <div 
      className="group bg-[#141414] rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-2 border border-[#D4AF37]/10 hover:border-[#D4AF37]/40 hover:shadow-xl hover:shadow-[#D4AF37]/10"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Product Image */}
      <Link to={`/shop/product/${product.slug}`} className="block relative aspect-square overflow-hidden bg-[#1A1A1A]">
        <img
          src={product.images[currentImage] || product.images[0]}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/800x800/1A1A1A/D4AF37?text=' + encodeURIComponent(product.name.substring(0, 20));
          }}
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {product.isNew && (
            <Badge className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#D4AF37]/90 font-body shadow-md font-bold">
              New
            </Badge>
          )}
          {hasDiscount && (
            <Badge className="bg-red-500 text-white hover:bg-red-500/90 font-body shadow-md font-bold">
              {discountPercent}% OFF
            </Badge>
          )}
          {product.isBestSeller && (
            <Badge className="bg-[#0A0A0A] text-[#D4AF37] border border-[#D4AF37]/50 font-body shadow-md">
              Best Seller
            </Badge>
          )}
        </div>

        {/* Wishlist Button */}
        <button
          data-testid={`wishlist-btn-${productId}`}
          onClick={(e) => {
            e.preventDefault();
            toggleWishlist(productId, product.name);
          }}
          className="absolute top-3 right-3 w-10 h-10 rounded-full bg-[#0A0A0A]/80 flex items-center justify-center hover:bg-[#D4AF37] transition-colors shadow-lg border border-[#D4AF37]/30 group/btn"
        >
          <Heart 
            className={`h-5 w-5 transition-colors ${
              isWishlisted ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-white group-hover/btn:text-[#0A0A0A]'
            }`} 
          />
        </button>

        {/* Quick View Button - appears on hover */}
        <button
          data-testid={`quick-view-btn-${productId}`}
          onClick={(e) => {
            e.preventDefault();
            setShowQuickView(true);
          }}
          className={`absolute bottom-3 right-3 px-3 py-2 rounded-lg bg-[#0A0A0A]/90 flex items-center gap-2 hover:bg-[#D4AF37] transition-all shadow-lg border border-[#D4AF37]/30 group/btn ${
            isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          }`}
        >
          <Eye className="h-4 w-4 text-[#D4AF37] group-hover/btn:text-[#0A0A0A]" />
          <span className="text-sm font-medium text-white group-hover/btn:text-[#0A0A0A] font-body">Quick View</span>
        </button>

        {/* Image Navigation Dots */}
        {product.images.length > 1 && isHovered && (
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-1.5">
            {product.images.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentImage(index);
                }}
                className={`h-1.5 rounded-full transition-all ${
                  currentImage === index 
                    ? 'bg-[#D4AF37] w-6' 
                    : 'bg-white/40 w-1.5 hover:bg-white/60'
                }`}
              />
            ))}
          </div>
        )}
      </Link>

      {/* Product Info */}
      <div className="p-4">
        <Link to={`/shop/product/${product.slug}`}>
          <h3 className="text-white font-medium mb-2 hover:text-[#D4AF37] transition-colors line-clamp-2 font-body">
            {product.name}
          </h3>
        </Link>

        {/* Rating */}
        {product.reviewCount > 0 && (
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center">
              <Star className="h-4 w-4 fill-[#D4AF37] text-[#D4AF37]" />
              <span className="text-sm text-white ml-1 font-medium font-body">{product.rating}</span>
            </div>
            <span className="text-sm text-[#888888] font-body">({product.reviewCount})</span>
          </div>
        )}

        {/* Category */}
        <p className="text-xs text-[#888888] mb-3 font-body">{product.category}</p>

        {/* Price */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xl font-bold text-[#D4AF37] font-body">${displayPrice.toFixed(2)}</span>
          {hasDiscount && (
            <span className="text-sm text-[#888888] line-through font-body">${product.price.toFixed(2)}</span>
          )}
        </div>

        {/* Add to Cart Button */}
        {product.inStock !== false ? (
          <Button 
            data-testid={`add-to-cart-btn-${productId}`}
            className="w-full bg-[#D4AF37] hover:bg-[#B8960C] text-[#0A0A0A] font-bold py-3 rounded-lg transition-all duration-300 font-body flex items-center justify-center gap-2"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              addToCart(productId, 1, product.name);
            }}
          >
            <ShoppingCart className="h-5 w-5" />
            <span>Add to Cart</span>
          </Button>
        ) : (
          <Button 
            disabled
            className="w-full bg-[#333333] text-[#888888] font-bold py-3 rounded-lg font-body flex items-center justify-center gap-2 cursor-not-allowed"
          >
            <span>Out of Stock</span>
          </Button>
        )}
      </div>

      {/* Quick View Modal */}
      <QuickViewModal 
        product={product} 
        isOpen={showQuickView} 
        onClose={() => setShowQuickView(false)} 
      />
    </div>
  );
};

export default SerenityProductCard;
